import * as React from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';
import { produce } from 'immer';

import {
    updateViewModelOrganization,
    updateFieldValidationErrorsByFieldId,
    handleEmailOnKeyDown,
    submitForm,
    getAvailableCountryChoices,
    getAvailableLocaleChoices,
    handleCountryOnKeyDown,
    getDefaultCountryChoice,
    isFormFilled,
    hasFailedInstantMatch,
    CountryComponent,
    ChangeLocaleComponent,
    MarketConsentWrapper,
    setRef,
    VerificationStepsEnum,
    setFocus,
    LogoComponent,
    PhoneNumberComponent as PhoneNumber,
    CollegeNameComponent as CollegeName,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    BirthDateComponent as BirthDate,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    FormFooterComponent as FormFooter,
    VerificationService,
    StudentPersonalInfoViewModel,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    Organization,
    FormSelectChoice,
    StudentPersonalInfoResponse,
    Locale,
    Country,
} from '@sheerid/jslib';

import { Disclaimer } from './Disclaimer';

interface StepStudentPersonalInfoComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

const StepStudentPersonalInfo = ({ intl, verificationService }: StepStudentPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as StudentPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const countryChoices: FormSelectChoice<Country, string>[] = getAvailableCountryChoices(verificationService.programTheme, intl);
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(verificationService.programTheme, intl);
    const defaultCountryChoice: FormSelectChoice<Country, string> = getDefaultCountryChoice(countryChoices);
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = { value: 'en-US', label: 'English' };
    const verificationResponse = verificationService.verificationResponse as StudentPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateStudentViewModel = (key: keyof StudentPersonalInfoViewModel, value: any) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            (draft[key] as any) = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            draft.localeChoice = localeChoice;
        });
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };

    const updateCountryChoice = (countryChoice: FormSelectChoice<Country, string>) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            draft.countryChoice = countryChoice;
        });
        verificationService.updateViewModel(nextState);
    };

    React.useEffect(() => {
        updateCountryChoice(defaultCountryChoice);
    },              []);

    return (
        <div id="sid-step-student-personal-info" className="sid-l-container">
            { failedInstantMatch ?
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Birthdate" />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name and birthdate must match what is on your official school records." />
                    </div>
                </div>
            :
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Student-Only Offer" />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center">
                            <FormattedHTMLMessage id="step.personalInfo.subtitle"
                                defaultMessage="Please, verify you're a college student 16+ years old." />
                        &nbsp;
                        <HowDoesVerifyingWork verificationService={verificationService} />
                    </div>
                </div>
            }

            {
                localeChoices.length > 1 ?
                    <ChangeLocaleComponent
                        options={localeChoices}
                        value={viewModel.localeChoice || defaultLocaleChoice}
                        isErrored={false}
                        onChange={(localeChoice: FormSelectChoice<Locale, string>) =>
                            updateLocale(localeChoice)}
                    />
                : null
            }

            {
                countryChoices.length > 1 ?
                    <CountryComponent
                        options={countryChoices}
                        value={viewModel.countryChoice || defaultCountryChoice}
                        isErrored={false}
                        onKeyDown={event => handleCountryOnKeyDown(event)}
                        onChange={(countryChoice: FormSelectChoice<Country, string>) => {
                            updateCountryChoice(countryChoice);
                            if (countryChoice && countryChoice.value) {
                                setFocus('organization');
                            }
                        }}
                    />
                : null
            }

            <CollegeName
                value={(verificationService.viewModel as StudentPersonalInfoViewModel).organization}
                placeholder={
                    intl.formatMessage({
                        id: 'collegeName',
                        defaultMessage: 'University/college name*',
                    })
                }
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    placeholder={
                        intl.formatMessage({
                            id: 'firstName',
                            defaultMessage: 'First Name*',
                        })
                    }
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue: string) => {
                            updateStudentViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName
                    value={viewModel.lastName}
                    placeholder={
                        intl.formatMessage({
                            id: 'lastName',
                            defaultMessage: 'Last Name*',
                        })
                    }
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue: string) => {
                            updateStudentViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue: any) => {
                    updateStudentViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <Email
                value={viewModel.email}
                placeholder={
                    intl.formatMessage({
                        id: 'emailAddress',
                        defaultMessage: 'Email Address*',
                    })
                }
                isErrored={!!fieldValidationErrors.email}
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Personal or school email is fine"
                    />
                }
                onChange={
                    (newValue: string) => {
                        updateStudentViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: any) => handleEmailOnKeyDown(event)}
            />

            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled ?
                    <PhoneNumber
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={
                            (newValue: any) => {
                                updateStudentViewModel('phoneNumber', newValue);
                                updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                            }
                        }
                    />
                    : null
            }

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateStudentViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-wrapper__collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectStudentPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.programTheme) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={(button: HTMLButtonElement) => setRef('submitButton', button)}
                    >
                        { failedInstantMatch ?
                            <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                        :
                            <FormattedHTMLMessage id="verifyMyStudentStatus" defaultMessage="Verify My Student Status" />
                        }
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService}>
                <Disclaimer />
                {
                    viewModel.countryChoice && viewModel.countryChoice.value !== 'US' ?
                        <FormattedHTMLMessage
                            id="informationTransferredToUS"
                            defaultMessage=
                                "Your information will be transferred to the United States and will be treated according to SheerID’s privacy policy."
                        />
                    : null
                }
            </FormFooter>
        </div>
    );
};

export const StepStudentPersonalInfoComponent = injectIntl(StepStudentPersonalInfo);
